import { useCallback, useState } from 'react';

const DEFAULT_PAGE_SIZE = 10;

export interface PaginationState {
  cursor?: string;
  pageIndex: number;
  pageIndexToCursorMap: Record<number, string>;
  pageSize: number;
}

export const usePaginationState = ({
  defaultPageSize = DEFAULT_PAGE_SIZE,
  initialCursor,
  initialPageIndex = 0,
}: { defaultPageSize?: number; initialCursor?: string; initialPageIndex?: number } = {}) => {
  const [pagination, setPagination] = useState<PaginationState>({
    cursor: initialCursor,
    pageIndex: initialPageIndex,
    pageIndexToCursorMap: initialCursor && initialPageIndex > 0 ? { [initialPageIndex]: initialCursor } : {},
    pageSize: defaultPageSize,
  });

  const onNext = useCallback(({ cursor }: { cursor: string }) => {
    setPagination((previous) => ({
      ...previous,
      cursor,
      pageIndex: previous.pageIndex + 1,
      pageIndexToCursorMap: {
        ...previous.pageIndexToCursorMap,
        [previous.pageIndex + 1]: cursor,
      },
    }));
  }, []);

  const onPrevious = useCallback(() => {
    setPagination((previous) => ({
      ...previous,
      cursor: previous.pageIndexToCursorMap[previous.pageIndex - 1],
      pageIndex: previous.pageIndex - 1,
    }));
  }, []);

  const setPageSize = useCallback((pageSize: number) => {
    setPagination((previous) => ({
      ...previous,
      pageSize,
      pageIndex: 0,
      pageIndexToCursorMap: {},
    }));
  }, []);

  const reset = useCallback(() => {
    setPagination((previous) => ({
      ...previous,
      cursor: undefined,
      pageIndex: 0,
      pageIndexToCursorMap: {},
    }));
  }, []);

  return {
    cursor: pagination.cursor,
    hasPrevious: pagination.pageIndex > 0,
    onNext,
    onPrevious,
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
    reset,
    setPageSize,
  };
};
